'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

export interface MeterConsumption {
	meterId: string;
	/** POD / codice del misuratore. */
	codice: string;
	/** Classificazione CACER: M1 prelievo · M2 produzione · M3 accumulo. */
	kind: string | null;
	tipo: string;
	memberId: string;
	/** `null` per un misuratore il cui socio non è più attivo. */
	memberName: string | null;
	memberType: string | null;
	/** Consumo del periodo, kWh. */
	prelevata: number;
	immessa: number;
	/** kWp installato su questo misuratore (impianti di produzione), null se non applicabile. */
	potenzaKwp: number | null;
	/**
	 * Quota % sul totale condiviso della configurazione (mock: "% Cond.") — in
	 * proporzione al prelievo del socio, non una media oraria per POD.
	 * `null` quando il socio non è fra i soci attivi (es. uscito).
	 */
	pctCondivisaConfig: number | null;
	/**
	 * % del consumo di questo POD coperta dalla quota condivisa attribuita al
	 * socio (mock: "AC" — Autoconsumo). `null` come sopra.
	 */
	pctAutoconsumo: number | null;
}

// Le stesse sorgenti che alimentano la riconciliazione di meter_energy_sums:
// una scrittura su una qualsiasi cambia la classifica per consumo.
const ENERGY_SOURCE_TABLES = [
	{ topic: 'energy-readings-realtime', table: 'energy_readings' },
	{ topic: 'estimated-data-realtime', table: 'estimated_data' },
	{ topic: 'gse-official-data-realtime', table: 'gse_official_data' },
	{ topic: 'energy-bill-readings-realtime', table: 'energy_bill_readings' },
] as const;

const EMPTY: MeterConsumption[] = [];

/**
 * Consumo per misuratore nel periodo, già ordinato per consumo decrescente e
 * corredato di socio e tipo socio (`GET /api/energy/consumption-by-meter`).
 *
 * ADR-006: backed by React Query — cache/dedup per (scope, intervallo).
 */
export function useConsumptionByMeter(
	orgId: string | null | undefined,
	range?: { from?: string; to?: string },
) {
	const queryClient = useQueryClient();
	const loaded = useAuthStore((s) => s.loaded);
	const enabled = loaded && !!orgId;
	const from = range?.from;
	const to = range?.to;

	const queryKey = ['consumption-by-meter', orgId, from ?? null, to ?? null] as const;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey,
		enabled,
		queryFn: () => {
			const params: Record<string, string> = { orgId: orgId as string };
			if (from) params.from = from;
			if (to) params.to = to;
			return api.get<MeterConsumption[]>('/api/energy/consumption-by-meter', params);
		},
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	// Le scritture di sorgente arrivano in blocco (import GSE, simulatore): un
	// evento per riga collasserebbe in una richiesta per riga senza il debounce.
	useEffect(() => {
		if (!enabled) return;
		let timer: ReturnType<typeof setTimeout> | null = null;
		const onChange = () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				queryClient.invalidateQueries({ queryKey: ['consumption-by-meter', orgId] });
			}, 800);
		};
		const unsubs = ENERGY_SOURCE_TABLES.map(({ topic, table }) =>
			subscribeToTable(topic, { event: '*', schema: 'public', table }, onChange),
		);
		return () => {
			if (timer) clearTimeout(timer);
			for (const unsub of unsubs) unsub();
		};
	}, [enabled, orgId, queryClient]);

	return {
		meters: Array.isArray(data) ? data : EMPTY,
		isLoading: enabled ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento consumi per misuratore'
			: null,
		refetch,
	};
}
